import { ApiService } from '@supply-mover-2-0/core';
import type { ApiResponseDataModel } from '@supply-mover-2-0/shared';

import { CartValidationManager } from './CartValidationManager';

interface CartItem {
	quantity: number;
	// PD-16171 — null when the line has no stored price. That is not the same
	// as $0.00: a rep may price a line at $0, and only a line with no price
	// at all may not be increased.
	price: number | string | null;
	// Preserved by useCartOperations when it round-trips items through here
	originalPrice?: number | string;
}

// Shape of the cart order payload as this file reads it. Some responses nest
// the order under a further `data` key (see `response.data.data ?? response.data`).
interface CartOrderPayloadModel {
	data?: CartOrderPayloadModel;
	orderDetails?: Array<{
		_fk_item__id?: number;
		quantityOrdered?: number;
		price?: number | string | null;
	}>;
}

interface CartStateData {
	items: Record<number, CartItem>;
	pendingPrices: Record<number, number>;
}

type CartListener = (cartItems: Record<number, CartItem>) => void;

type PendingPriceListener = () => void;

class SharedCartState {
	private cartData: Record<string, CartStateData> = {}; // clientId -> cart state data
	private readonly listeners: Map<string, Set<CartListener>> = new Map();
	// PD-16171 — per-item pending-price subscribers. The cart listeners above
	// receive the same (mutated) items object on every notify, so React skips
	// the re-render; a shop cell that must react to a price typed in ANOTHER
	// cell (the Add button enabling once a rep enters a price for an unpriced
	// item) subscribes here instead. Keyed `${clientId}:${itemId}` so a
	// keystroke re-renders only that row.
	private readonly _pendingPriceListeners: Map<
		string,
		Set<PendingPriceListener>
	> = new Map();

	// Get cart items for a specific client
	getCartItems(clientId: string): Record<number, CartItem> {
		return this.cartData[clientId]?.items ?? {};
	}

	// Update cart items for a specific client
	updateCartItems(clientId: string, items: Record<number, CartItem>) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		this.cartData[clientId].items = items;
		this.notifyListeners(clientId);
	}

	// Pending price methods
	setPendingPrice(clientId: string, itemId: number, price: number) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		this.cartData[clientId].pendingPrices[itemId] = price;
		// Notify listeners so components re-render with the new pending price
		this.notifyListeners(clientId);
		this._notifyPendingPriceListeners(clientId, itemId);
	}

	getPendingPrice(clientId: string, itemId: number): number | undefined {
		return this.cartData[clientId]?.pendingPrices[itemId];
	}

	clearPendingPrice(clientId: string, itemId: number) {
		if (this.cartData[clientId]?.pendingPrices != null) {
			delete this.cartData[clientId].pendingPrices[itemId];
		}

		this._notifyPendingPriceListeners(clientId, itemId);
	}

	/**
	 * Subscribe to one item's pending price (see _pendingPriceListeners).
	 * Returns the unsubscribe function, the shape useSyncExternalStore wants.
	 */
	public subscribePendingPrice(
		clientId: string,
		itemId: number,
		listener: PendingPriceListener,
	): () => void {
		const key = `${clientId}:${String(itemId)}`;
		let itemListeners = this._pendingPriceListeners.get(key);

		if (itemListeners == null) {
			itemListeners = new Set();
			this._pendingPriceListeners.set(key, itemListeners);
		}

		itemListeners.add(listener);

		return () => {
			const current = this._pendingPriceListeners.get(key);

			if (current != null) {
				current.delete(listener);

				if (current.size === 0) {
					this._pendingPriceListeners.delete(key);
				}
			}
		};
	}

	private _notifyPendingPriceListeners(clientId: string, itemId: number): void {
		const itemListeners = this._pendingPriceListeners.get(
			`${clientId}:${String(itemId)}`,
		);

		itemListeners?.forEach((listener) => {
			try {
				listener();
			} catch {
				// A failing subscriber must not stop the others.
			}
		});
	}

	// Update a single item in the cart
	updateCartItem(clientId: string, itemId: number, item: CartItem | null) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		if (item === null) {
			delete this.cartData[clientId].items[itemId];
		} else {
			this.cartData[clientId].items[itemId] = item;
		}

		this.notifyListeners(clientId);
	}

	// Subscribe to cart changes for a specific client
	subscribe(clientId: string, listener: CartListener): () => void {
		if (!this.listeners.has(clientId)) {
			this.listeners.set(clientId, new Set());
		}

		this.listeners.get(clientId)!.add(listener);

		// Immediately call listener with current state
		listener(this.getCartItems(clientId));

		// Return unsubscribe function
		return () => {
			const clientListeners = this.listeners.get(clientId);

			if (clientListeners != null) {
				clientListeners.delete(listener);

				if (clientListeners.size === 0) {
					this.listeners.delete(clientId);
				}
			}
		};
	}

	// Load cart from server
	async loadCart(clientId: string): Promise<void> {
		try {
			const response = await ApiService.apiGet<
				ApiResponseDataModel<CartOrderPayloadModel>
			>(`/orders/cart/${clientId}`);

			if (response.success && response.data != null) {
				const cartData: Record<number, CartItem> = {};

				// Get the actual cart data (nested under response.data.data)
				const cartResponse = response.data.data ?? response.data;

				// Convert order details to cart items map
				if (
					cartResponse.orderDetails != null &&
					Array.isArray(cartResponse.orderDetails)
				) {
					cartResponse.orderDetails.forEach(
						(detail: {
							_fk_item__id?: number;
							quantityOrdered?: number;
							price?: number | string | null;
						}) => {
							if (detail._fk_item__id != null) {
								cartData[detail._fk_item__id] = {
									quantity: detail.quantityOrdered ?? 0,
									price: detail.price ?? null,
								};
							}
						},
					);
				}

				this.updateCartItems(clientId, cartData);
			} else {
				this.updateCartItems(clientId, {});
			}
		} catch (err) {
			// Don't update state on error, keep existing cart items
		}
	}

	// Clear cart for a client
	clearCart(clientId: string) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		} else {
			this.cartData[clientId].items = {};
		}

		// Clear CartValidationManager state to remove any cached validation states
		CartValidationManager.cleanup();

		this.notifyListeners(clientId);
	}

	private notifyListeners(clientId: string) {
		const clientListeners = this.listeners.get(clientId);

		if (clientListeners != null) {
			const cartItems = this.getCartItems(clientId);

			clientListeners.forEach((listener) => {
				try {
					listener(cartItems);
				} catch (error) {
					// Error: SharedCartState Error in cart listener
				}
			});
		}
	}
}

// Export singleton instance
export const sharedCartState = new SharedCartState();
